<template>
  <div class="main-content">
    <div class="chart-section">
      <div class="chart-filter">
        <h2>Gráficos Mensais</h2>
        <div class="filters">
          <label for="year">Ano:</label>
          <select
            v-model="selectedYear"
            @change="fetchMonthlyData"
          >
            <option
              v-for="year in availableYears"
              :key="year"
              :value="year"
            >
              {{ year }}
            </option>
          </select>

          <label for="month">Mês:</label>
          <select
            v-model="selectedMonth"
            :disabled="!selectedYear"
            @change="fetchMonthlyData"
          >
            <option
              value=""
              disabled
            >
              Selecione um mês
            </option>
            <option
              v-for="month in filteredMonths"
              :key="month.value"
              :value="month.value"
            >
              {{ month.label }}
            </option>
          </select>
        </div>
      </div>

      <div class="charts-wrapper">
        <div class="chart-card">
          <h3 class="chart-title">
            Interesse Mensal
          </h3>
          <horizontal-bar-chart
            :chart-data="monthlyInterest"
            :chart-options="monthChartOptions"
          />
        </div>
        <div class="chart-card">
          <h3 class="chart-title">
            Fases Mensais
          </h3>
          <horizontal-bar-chart
            :chart-data="monthlyPhases"
            :chart-options="monthChartOptions"
          />
        </div>
        <div class="chart-card-extended">
          <h3 class="chart-title">
            Top Licitações Mensais
          </h3>
          <horizontal-bar-chart
            :chart-data="monthlyBidding"
            :chart-options="monthChartOptions"
          />
        </div>
      </div>
    </div>

    <div class="chart-section-2">
      <div class="chart-filter">
        <h2>Gráficos Anuais</h2>
        <div class="filters">
          <label for="year-annual">Ano:</label>
          <select
            v-model="selectedYearAnnual"
            @change="fetchAnnualData"
          >
            <option value="">
              Selecione um ano
            </option>
            <option
              v-for="year in availableYears"
              :key="year"
              :value="year"
            >
              {{ year }}
            </option>
          </select>
          <span v-if="loadingAnnualInterest">Carregando...</span>
        </div>
      </div>

      <div class="charts-wrapper">
        <div class="chart-card">
          <h3 class="chart-title">
            Interesse Anual
          </h3>
          <horizontal-bar-chart
            :chart-data="annualInterest"
            :chart-options="yearChartOptions"
          />
        </div>
        <div class="chart-card">
          <h3 class="chart-title">
            Fases Anuais
          </h3>
          <horizontal-bar-chart
            :chart-data="annualPhases"
            :chart-options="yearChartOptions"
          />
        </div>
        <div class="chart-card">
          <h3 class="chart-title">
            Quantidade de Licitações Anuais
          </h3>
          <vertical-bar-chart
            :chart-data="annualCount"
            :chart-options="yearChartOptions"
          />
        </div>
        <div class="chart-card">
          <h3 class="chart-title">
            Receita Anual
          </h3>
          <vertical-bar-chart
            :chart-data="annualEarnings"
            :chart-options="yearChartOptions"
          />
        </div>
        <div class="chart-card-extended">
          <h3 class="chart-title">
            Top Licitações Anuais
          </h3>
          <horizontal-bar-chart
            :chart-data="annualBidding"
            :chart-options="yearChartOptions"
          />
        </div>
      </div>
    </div>

    <div class="chart-section-3">
      <div class="charts-wrapper">
        <div class="chart-card-extended-2">
          <h3 class="chart-title">
            Desempenho do Funil de Licitações
          </h3>
          <chart-efficiency />
        </div>
        <div class="chart-card-extended-2">
          <h3 class="chart-title">
            Licitações por Portal
          </h3>
          <doughnut-chart :chart-data="portalPerformanceData" />
        </div>
      </div>
    </div>

    <div class="chart-section-4">
      <div class="charts-wrapper">
        <div class="chart-card-2">
          <h3 class="chart-title">
            Licitações por Estado
          </h3>
          <regional-bidding-chart />
        </div>
      </div>
    </div>

    <div class="chart-section-5">
      <div class="charts-wrapper">
        <div class="chart-card-2">
          <h3 class="chart-title">
            Licitações por Itens
          </h3>
          <stacked-bar-chart />
        </div>
      </div>
    </div>

    <div class="chart-section-6">
      <div class="charts-wrapper">
        <div class="chart-card-2">
          <h3 class="chart-title">
            Desempenho de Licitações Ganhas
          </h3>
          <performance-bar-chart />
        </div>
      </div>
    </div>

    <div class="chart-section-7">
      <div class="charts-wrapper">
        <div class="chart-card-2">
          <h3 class="chart-title">
            Receita Mensal
          </h3>
          <monthly-revenue-chart />
        </div>
      </div>
    </div>

    <div class="chart-section-8">
      <div class="charts-wrapper">
        <div class="chart-card3">
          <h3 class="chart-title">
            Fases da Licitação
          </h3>
          <loss-reasons-pie-chart />
        </div>
        <div class="chart-card3">
          <h3 class="chart-title">
            Tempo de Conclusão das Licitações
          </h3>
          <process-time-per-phase-chart />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import fetchAnnualData from '@/syslic/dashboard/reportYearService'
import fetchMonthlyData from '@/syslic/dashboard/reportMonthlyService'
import filterMonthlyInterest from '@/syslic/dashboard/monthlyInterestFilter'
import filterMonthlyPhases from '@/syslic/dashboard/monthlyPhasesFilter'
import filterMonthlyBidding from '@/syslic/dashboard/monthlyBiddingFilter'
import filterAnnualInterest from '@/syslic/dashboard/annualInterestFilter'
import filterAnnualPhases from '@/syslic/dashboard/annualPhasesFilter'
import filterAnnualBidding from '@/syslic/dashboard/annualBiddingFilter'
import filterAnnualCount from '@/syslic/dashboard/annualCountsFilter'
import filterAnnualEarnings from '@/syslic/dashboard/annualEarningsFilter'
import filterPortalBiddingData from '@/syslic/dashboard/portal/portalBiddingFilter'
import HorizontalBarChart from '@/components/dashboard/charts/HorizontalBarChart.vue'
import VerticalBarChart from '@/components/dashboard/charts/VerticalBarChart.vue'
import RegionalBiddingChart from '@/components/dashboard/charts/RegionalBiddingChart.vue'
import DoughnutChart from '@/components/dashboard/charts/DoughnutChart.vue'
import ChartEfficiency from '@/components/dashboard/charts/ChartEfficiency.vue'
import StackedBarChart from '@/components/dashboard/charts/StackedBarChart.vue'
import PerformanceBarChart from '@/components/dashboard/charts/PerformanceBarChart.vue'
import MonthlyRevenueChart from '@/components/dashboard/charts/MonthlyRevenueChart.vue'
import LossReasonsPieChart from '@/components/dashboard/charts/LossReasonsPieChart.vue'
import ProcessTimePerPhaseChart from '@/components/dashboard/charts/ProcessTimePerPhaseChart.vue'

export default {
  components: {
    HorizontalBarChart,
    VerticalBarChart,
    RegionalBiddingChart,
    DoughnutChart,
    ChartEfficiency,
    StackedBarChart,
    PerformanceBarChart,
    MonthlyRevenueChart,
    LossReasonsPieChart,
    ProcessTimePerPhaseChart,
  },
  data() {
    const currentDate = new Date()
    const currentYear = currentDate.getFullYear()
    const currentMonth = currentDate.getMonth() + 1 // getMonth é 0-based

    return {
      monthlyInterest: { labels: [], datasets: [] },
      monthlyPhases: { labels: [], datasets: [] },
      monthlyBidding: { labels: [], datasets: [] },
      annualInterest: { labels: [], datasets: [] },
      annualBidding: { labels: [], datasets: [] },
      annualPhases: { labels: [], datasets: [] },
      annualCount: { labels: [], datasets: [] },
      annualEarnings: { labels: [], datasets: [] },
      regionalBidding: { labels: [], datasets: [] },
      portalPerformanceData: { labels: [], datasets: [] }, // <-- Inicializa donut
      biddingLimit: 15,
      selectedYearAnnual: currentDate.getFullYear(),
      availableYears: this.getAvailableYears(),
      loadingMonthlyInterest: false,
      loadingAnnualInterest: false,
      selectedYear: currentYear,
      selectedMonth: currentMonth,
      regionChartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'top',
          },
          title: {
            display: true,
            text: 'Licitações por Região',
          },
        },
        scales: {
          x: {
            beginAtZero: true,
          },
        },
      },
      monthChartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'top',
          },
          title: {
            display: true,
            text: 'Interesse no Mês',
          },
        },
        scales: {
          x: {
            beginAtZero: true,
          },
        },
      },
      yearChartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'top',
          },
          title: {
            display: true,
            text: 'Interesse no Ano',
          },
        },
        scales: {
          x: {
            beginAtZero: true,
          },
        },
      },
    }
  },
  computed: {
    filteredMonths() {
      const allMonths = [
        { value: 1, label: 'Janeiro' },
        { value: 2, label: 'Fevereiro' },
        { value: 3, label: 'Março' },
        { value: 4, label: 'Abril' },
        { value: 5, label: 'Maio' },
        { value: 6, label: 'Junho' },
        { value: 7, label: 'Julho' },
        { value: 8, label: 'Agosto' },
        { value: 9, label: 'Setembro' },
        { value: 10, label: 'Outubro' },
        { value: 11, label: 'Novembro' },
        { value: 12, label: 'Dezembro' },
      ]

      const currentYear = new Date().getFullYear()
      const currentMonth = new Date().getMonth() + 1

      if (this.selectedYear === currentYear) {
        return allMonths.filter(m => m.value <= currentMonth)
      }

      return allMonths
    },
  },
  created() {
    this.fetchAnnualData()
    if (this.selectedYear && this.selectedMonth) {
      this.fetchMonthlyData()
    }
  },
  methods: {
    getAvailableYears() {
      const currentYear = new Date().getFullYear()
      const years = []
      for (let i = 0; i < 5; i += 1) {
        years.push(currentYear - i)
      }
      return years
    },

    async fetchAnnualData() {
      if (!this.selectedYearAnnual) {
        this.resetChartData()
        return
      }

      this.loadingAnnualInterest = true
      try {
        const data = await fetchAnnualData(this.selectedYearAnnual, this) // Passando a instância do componente
        this.annualInterest = filterAnnualInterest(data)
        this.annualBidding = filterAnnualBidding(data, this.biddingLimit)
        this.annualPhases = filterAnnualPhases(data)
        this.annualEarnings = filterAnnualEarnings(data)
        this.annualCount = filterAnnualCount(data)

        // Atualiza dados do gráfico donut após carregar dados anuais
        this.portalPerformanceData = filterPortalBiddingData(data, {
          portal: this.portalSelecionado || null, // ou outro nome da variável do portal
          startDate: null,
          endDate: null,
        })
      } catch (error) {
        let textError = 'Não foi possível carregar os dados anuais. Por favor, tente novamente ou entre em contato com o suporte.'

        if (error.response) {
          if (error.response.status === 404) {
            textError = 'Dados não encontrados para o ano selecionado'
          } else if (error.response.data?.message) {
            textError = error.response.data.message
          }
        }

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao buscar dados anuais',
            text: textError,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            timeout: 5000, // Alterado para fechar automaticamente após 5 segundos
          },
        })
      } finally {
        this.loadingAnnualInterest = false
      }
    },

    async fetchMonthlyData() {
      if (!this.selectedYear || !this.selectedMonth) {
        return
      }

      this.loadingMonthlyInterest = true
      try {
        const data = await fetchMonthlyData(this.selectedYear, this.selectedMonth, this) // Passando a instância
        this.monthlyInterest = filterMonthlyInterest(data)
        this.monthlyPhases = filterMonthlyPhases(data)
        this.monthlyBidding = filterMonthlyBidding(data, this.biddingLimit)
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao buscar dados mensais',
            text: 'Não foi possível carregar os dados. Tente novamente ou entre em contato com o suporte.',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            timeout: 5000,
          },
        })
      } finally {
        this.loadingMonthlyInterest = false
      }
    },

    resetChartData() {
      this.annualInterest = { labels: [], datasets: [] }
      this.annualBidding = { labels: [], datasets: [] }
      this.annualPhases = { labels: [], datasets: [] }
      this.annualCount = { labels: [], datasets: [] }
      this.annualEarnings = { labels: [], datasets: [] }
      this.monthlyInterest = { labels: [], datasets: [] }
      this.monthlyPhases = { labels: [], datasets: [] }
      this.monthlyBidding = { labels: [], datasets: [] }
      this.portalPerformanceData = { labels: [], datasets: [] }
    },
  },
}
</script>

<style scoped>
.chart-title {
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 1rem;
  text-align: center;
}

.main-content {
  gap: 1rem;
}

.chart-section-2, .chart-section-3, .chart-section-4, .chart-section-5, .chart-section-6, .chart-section-7, .chart-section-8 {
  margin-top: 1rem;
}

.filters {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  align-items: center;
}

.filters label {
  font-weight: bold;
}

.filters select {
  padding: 0.5rem;
  font-size: 1rem;
  border-radius: 0.25rem;
  border: 1px solid #ccc;
}

.chart-filter {
  width: 100%;
  height: 100%;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: #fff;
  border-radius: 0.25rem;
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 1rem;
}

.charts-wrapper {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.chart-card {
  flex: 1 1 45%;
  background-color: #fff;
  border-radius: 0.25rem;
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.1);
  padding: 1rem;
  width: 100%;
  height: 470px;
}

.chart-card-2 {
  flex: 1 1 45%;
  background-color: #fff;
  border-radius: 0.25rem;
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.1);
  padding: 1rem;
  width: 100%;
  height: 470px;
}

.chart-card3 {
  flex: 1 1 45%;
  background-color: #fff;
  border-radius: 0.25rem;
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.1);
  padding: 1rem;
  width: 100%;
  height: 600px;
}

.chart-card-extended {
  flex: 1 1 45%;
  background-color: #fff;
  border-radius: 0.25rem;
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.1);
  padding: 1rem;
  width: 100%;
  height: 480px;
}

.chart-card-extended-2 {
  flex: 1 1 45%;
  background-color: #fff;
  border-radius: 0.25rem;
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.1);
  padding: 1rem;
  width: 100%;
  height: 560px;
}

@media (min-width: 320px) {
  .chart-card-extended-2 {
    width: 100%;
    height: 630px;
  }
  .chart-card-2 {
    width: 100%;
    height: 660px;
  }
  .chart-card3 {
    width: 100%;
    height: 670px;
  }
}

@media (min-width: 375px) {
  .chart-card-extended-2 {
    width: 100%;
    height: 700px;
  }
  .chart-card3 {
    width: 100%;
    height: 630px;
  }
}

@media (min-width: 425px) {
  .chart-card-extended-2 {
    height: 610px;
  }
}

@media (min-width: 768px) {
  .chart-card-extended-2 {
    height: 600px;
  }
  .chart-card-2 {
    width: 100%;
    height: 560px;
  }
  .chart-card-3 {
    width: 100%;
    height: 550px;
  }
}
</style>
